/**
 * TMProfilePage — member's own profile + sign out
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Mail, Phone, ShieldCheck, User } from 'lucide-react';
import toast from 'react-hot-toast';
import { signOut } from 'firebase/auth';
import { auth } from '@/config/firebase';
import { useTMAuth } from '@/contexts/TMAuthContext';
import { updateDoc, doc } from 'firebase/firestore';
import { db } from '@/config/firebase';

export default function TMProfilePage() {
  const { tmUser, isTMAdmin } = useTMAuth();
  const navigate = useNavigate();
  const [editPhone, setEditPhone] = useState(false);
  const [phone,     setPhone]     = useState(tmUser?.phone ?? '');
  const [saving,    setSaving]    = useState(false);

  if (!tmUser) return null;

  const handleSavePhone = async () => {
    setSaving(true);
    await updateDoc(doc(db, 'tmUsers', tmUser.uid), { phone });
    toast.success('Phone updated');
    setSaving(false);
    setEditPhone(false);
  };

  const handleSignOut = async () => {
    await signOut(auth);
    navigate('/tm');
  };

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold text-gray-900">My Profile</h1>

      {/* Avatar card */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex flex-col items-center gap-3">
        <div className="w-20 h-20 rounded-full bg-indigo-100 flex items-center justify-center">
          <span className="text-3xl font-bold text-indigo-600">{tmUser.name.charAt(0).toUpperCase()}</span>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold text-gray-900">{tmUser.name}</p>
          <span className={`mt-1 inline-block text-xs font-semibold px-3 py-1 rounded-full ${
            isTMAdmin ? 'bg-purple-100 text-purple-700' : 'bg-indigo-50 text-indigo-600'}`}>
            {isTMAdmin ? '★ Admin' : 'Member'}
          </span>
        </div>
      </div>

      {/* Details */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm divide-y divide-gray-100">
        {/* Email */}
        <div className="flex items-center gap-3 px-5 py-4">
          <Mail className="h-4 w-4 text-gray-400 shrink-0" />
          <div>
            <p className="text-xs text-gray-400">Email</p>
            <p className="text-sm font-medium text-gray-800">{tmUser.email}</p>
          </div>
        </div>

        {/* Phone */}
        <div className="flex items-center gap-3 px-5 py-4">
          <Phone className="h-4 w-4 text-gray-400 shrink-0" />
          <div className="flex-1">
            <p className="text-xs text-gray-400">Phone</p>
            {editPhone ? (
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
                <button onClick={handleSavePhone} disabled={saving}
                  className="text-xs px-3 py-1.5 bg-indigo-600 text-white rounded-lg disabled:opacity-60">
                  {saving ? '…' : 'Save'}
                </button>
                <button onClick={() => setEditPhone(false)} className="text-xs px-2 py-1.5 text-gray-400">
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-800">{tmUser.phone || '—'}</p>
                <button onClick={() => setEditPhone(true)}
                  className="text-xs text-indigo-500 font-medium">
                  Edit
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Role */}
        <div className="flex items-center gap-3 px-5 py-4">
          <ShieldCheck className="h-4 w-4 text-gray-400 shrink-0" />
          <div>
            <p className="text-xs text-gray-400">Role</p>
            <p className="text-sm font-medium text-gray-800 capitalize">{tmUser.role}</p>
          </div>
        </div>

        {/* Member since */}
        <div className="flex items-center gap-3 px-5 py-4">
          <User className="h-4 w-4 text-gray-400 shrink-0" />
          <div>
            <p className="text-xs text-gray-400">Member since</p>
            <p className="text-sm font-medium text-gray-800">
              {tmUser.approvedAt
                ? tmUser.approvedAt.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
                : '—'}
            </p>
          </div>
        </div>
      </div>

      {/* Sign out */}
      <button
        onClick={handleSignOut}
        className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl border border-red-200 text-red-500 text-sm font-semibold active:scale-95 transition-all">
        <LogOut className="h-4 w-4" />
        Sign Out
      </button>
    </div>
  );
}
